import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    OnInit,
    output,
} from '@angular/core';
import { IDateRangeSelection } from '@dxp/core/models';
import {
    NgbCalendar,
    NgbDate,
    NgbDatepickerModule,
    NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { addYears, formatDate } from 'date-fns';

@Component({
    selector: 'telxl-datepicker',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, NgbDatepickerModule],
    templateUrl: './datepicker.component.html',
    styleUrl: './datepicker.component.scss',
})
export class TelXLDatepickerComponent implements OnInit {
    readonly #calendar = inject(NgbCalendar);

    isClearHovered = false;
    isInputHovered = false;

    allowEmptySelection = input<boolean>(true);
    fromDate = input<Date | null>(null);
    toDate = input<Date | null>(null);
    maxDate = input<Date | null>(null);
    minDate = input<Date | null>(null);
    range = input<boolean>(false);
    outsideDays = input<'visible' | 'hidden' | 'collapsed'>('visible');
    autoClose = input<boolean | 'inside' | 'outside'>(true);

    #hoveredDate: NgbDate | null = null;
    #fromDate: NgbDate | null = null;
    #toDate: NgbDate | null = null;

    dateRangeSelected = output<IDateRangeSelection>();
    dateSelected = output<Date | null>();

    constructor() {
        effect(() => {
            this.#fromDate = this.#toNgbDate(this.fromDate());
            this.#toDate = this.#toNgbDate(this.toDate());
        });
    }

    ngOnInit() {
        this.#fromDate = this.#toNgbDate(this.fromDate());
        this.#toDate = this.#toNgbDate(this.toDate());
    }

    onDateSelection(date: NgbDate) {
        this.#fromDate = date;
        this.dateSelected.emit(this.#fromNgbDate(this.#fromDate));
    }

    onDateRangeSelection(date: NgbDate) {
        if (!this.#fromDate && !this.#toDate) {
            this.#fromDate = date;
        } else if (
            this.#fromDate &&
            !this.#toDate &&
            date.after(this.#fromDate)
        ) {
            this.#toDate = date;
        } else {
            this.#toDate = null;
            this.#fromDate = date;
        }

        this.dateRangeSelected.emit({
            from: this.#fromNgbDate(this.#fromDate),
            to: this.#fromNgbDate(this.#toDate),
        });
    }

    onClosed() {
        if (this.range() && (!this.#fromDate || !this.#toDate)) {
            this.#fromDate = this.allowEmptySelection()
                ? null
                : this.#toNgbDate(this.fromDate());
            this.#toDate = this.allowEmptySelection()
                ? null
                : this.#toNgbDate(this.fromDate());

            this.dateRangeSelected.emit({
                from: this.#fromNgbDate(this.#fromDate),
                to: this.#fromNgbDate(this.#toDate),
            });
        }
    }

    isHovered(date: NgbDate): boolean {
        return !!(
            this.#fromDate &&
            !this.#toDate &&
            this.#hoveredDate &&
            date.after(this.#fromDate) &&
            date.before(this.#hoveredDate)
        );
    }

    isInside(date: NgbDate): boolean {
        return !!(
            this.#toDate &&
            date.after(this.#fromDate) &&
            date.before(this.#toDate)
        );
    }

    isRange(date: NgbDate): boolean {
        return (
            date.equals(this.#fromDate) ||
            (this.#toDate && date.equals(this.#toDate)) ||
            this.isInside(date) ||
            this.isHovered(date)
        );
    }

    isSelected(date: NgbDate): boolean {
        return date.equals(this.#fromDate);
    }

    clear(event: Event) {
        if (this.hasValue) {
            this.#fromDate = null;
            this.#toDate = null;

            if (this.range()) {
                this.dateRangeSelected.emit({
                    from: this.#fromNgbDate(this.#fromDate),
                    to: this.#fromNgbDate(this.#toDate),
                });
            } else {
                this.dateSelected.emit(this.#fromNgbDate(this.#fromDate));
            }

            event.stopPropagation();
            event.preventDefault();
        }
    }

    #toNgbDate(date?: Date | null): NgbDate | null {
        return date
            ? new NgbDate(
                  date.getFullYear(),
                  date.getMonth() + 1,
                  date.getDate(),
              )
            : null;
    }

    #toNgbDateStruct(date: Date): NgbDateStruct {
        return {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
        };
    }

    #fromNgbDate(ngbDate?: NgbDate | null): Date | null {
        return ngbDate
            ? new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day)
            : null;
    }

    get fromDateText(): string {
        const from = this.#fromNgbDate(this.#fromDate);
        return from ? formatDate(from, 'dd/MM/yy') : 'Start Date';
    }

    get toDateText(): string {
        const to = this.#fromNgbDate(this.#toDate);
        return to ? formatDate(to, 'dd/MM/yy') : 'End Date';
    }

    get hoveredDate(): NgbDate | null {
        return this.#hoveredDate;
    }

    set hoveredDate(date: NgbDate | null) {
        this.#hoveredDate = date;
    }

    get startDate(): NgbDate {
        return this.#fromDate ?? this.#calendar.getToday();
    }

    get minimumDate(): NgbDateStruct {
        return this.minDate()
            ? this.#toNgbDateStruct(this.minDate() as Date)
            : this.#toNgbDateStruct(addYears(new Date(), -10));
    }

    get maximumDate(): NgbDateStruct {
        return this.maxDate()
            ? this.#toNgbDateStruct(this.maxDate() as Date)
            : this.#toNgbDateStruct(addYears(new Date(), 10));
    }

    get hasValue(): boolean {
        if (this.range()) {
            return this.#toDate !== null && this.#fromDate !== null;
        } else {
            return this.#fromDate !== null;
        }
    }
}
